import {
  FrameSink,
  OUTPUT_SIZES,
  RecordingFormat,
  downloadRecording,
} from '../recorder-core';

interface RecorderDeps {
  getSourceCanvas: () => HTMLCanvasElement | null;
  getOverlayCanvas: () => HTMLCanvasElement | null;
  getMediaElement: () => HTMLMediaElement | null;
}

type CapturableVideoTrack = MediaStreamTrack & {
  requestFrame?: () => void;
};

declare const MediaStreamTrackProcessor: {
  new (init: { track: MediaStreamTrack }): { readable: ReadableStream<VideoFrame> };
};

export class PanoRecorder {
  private deps: RecorderDeps;
  // Output half (composite canvas, MediaRecorder, encoding, teardown) lives in
  // FrameSink; this class owns only the panorama-specific frame source.
  private sink = new FrameSink();
  // Source pipeline: PSV's WebGL canvas → captureStream → processor → reader.
  // Reading the WebGL canvas via the captureStream pipeline (rather than
  // synchronous drawImage in our JS) lets the browser handle the WebGL paint
  // timing internally — the VideoFrames we receive are guaranteed to be fully
  // painted, eliminating the partial-paint tearing we'd otherwise see at the
  // top of the output during camera movement (preserveDrawingBuffer is false
  // on PSV's renderer, so direct drawImage is unreliable per the WebGL spec).
  private sourceCaptureStream: MediaStream | null = null;
  private sourceTrack: CapturableVideoTrack | null = null;
  private sourceReader: ReadableStreamDefaultReader<VideoFrame> | null = null;
  private pipelineActive = false;
  private pipelineTask: Promise<void> | null = null;
  // True when the WebCodecs source pipeline is running, so captureFrame()
  // composites from `latestInputFrame` rather than reading the WebGL canvas
  // directly (which would risk the tearing described above).
  private useProcessorInput = false;
  // Most recent input VideoFrame from the source captureStream — replaced as
  // new frames arrive, consumed (composited + duplicated as needed) by
  // captureFrame() at the output target rate.
  private latestInputFrame: VideoFrame | null = null;

  constructor(deps: RecorderDeps) {
    this.deps = deps;
  }

  start(format: RecordingFormat, fps: number): void {
    const safeFps = Number.isFinite(fps) && fps > 0 ? Math.round(fps) : 30;

    const hasProcessor = typeof (globalThis as any).MediaStreamTrackProcessor === 'function';
    const hasVideoFrame = typeof (globalThis as any).VideoFrame === 'function';
    const sourceCanvas = this.deps.getSourceCanvas();

    if (hasProcessor && hasVideoFrame && sourceCanvas) {
      // === Tear-free source pipeline, decoupled input/output rates ===
      // Source uses captureStream(safeFps * 2) — browser-driven continuous
      // emission at TWICE the target rate. Oversampling the input avoids the
      // phase-mismatch duplicates we'd see if the source and the output ticker
      // ran at the same nominal rate: even though both average to ~25 Hz, the
      // independent timers drift, and ticks that land just before a new input
      // arrives would otherwise emit the previous frame again. With 2× input
      // rate, there's always a fresh frame ready by the time the output
      // ticker fires.
      // The browser handles WebGL paint timing, so each emitted frame is
      // guaranteed complete (no tearing). A background reader task keeps the
      // latest received input frame in memory; the output side is driven by
      // captureFrame() at exactly target FPS. If the source briefly stalls
      // (e.g., paused video), the output side reuses the latest input —
      // guaranteeing constant output FPS.
      this.sourceCaptureStream = sourceCanvas.captureStream(safeFps * 2);
      const sourceTracks = this.sourceCaptureStream.getVideoTracks();
      this.sourceTrack = (sourceTracks[0] ?? null) as CapturableVideoTrack | null;
      if (this.sourceTrack) {
        const processor = new MediaStreamTrackProcessor({ track: this.sourceTrack });
        this.sourceReader = processor.readable.getReader();
        this.useProcessorInput = true;
        this.pipelineActive = true;
        this.pipelineTask = this.runReaderLoop();
      }
    }

    // Falling through with useProcessorInput === false means no
    // MediaStreamTrackProcessor: captureFrame() reads the WebGL canvas
    // directly via drawImage. Subject to the tearing risk above, but it's the
    // best we can do in browsers without the WebCodecs pipeline APIs.

    try {
      this.sink.start(format, safeFps);
    } catch (err) {
      // Don't strand the source pipeline if the encoder failed to come up.
      this.teardownSource();
      throw err;
    }
  }

  /**
   * Cadence signal from the driver. Composites the latest source frame (plus
   * the drawing overlay) onto the output canvas and hands it to the sink. If
   * the source hasn't emitted a frame yet (early in recording) the call is a
   * no-op; if no new input has arrived since the previous call (paused
   * playback), the latest frame is duplicated so the recording timeline keeps
   * advancing.
   */
  captureFrame(): void {
    this.sink.captureFrame((ctx, width, height) => {
      const source: CanvasImageSource | null = this.useProcessorInput
        ? this.latestInputFrame
        : this.deps.getSourceCanvas();
      if (!source) return false; // source hasn't emitted yet
      try {
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(source, 0, 0, width, height);
        const overlay = this.deps.getOverlayCanvas();
        if (overlay && overlay.width > 0 && overlay.height > 0) {
          ctx.drawImage(overlay, 0, 0, width, height);
        }
      } catch { /* composite error; emit whatever the canvas holds */ }
      return true;
    });
  }

  async stop(): Promise<{ blob: Blob; mimeType: string } | null> {
    await this.shutdownSource();
    return this.sink.stop();
  }

  download(blob: Blob, mimeType: string, format: RecordingFormat): void {
    downloadRecording(blob, mimeType, format, 'pano');
  }

  isRecording(): boolean {
    return this.sink.isRecording();
  }

  /**
   * Reader loop: continuously pulls VideoFrames from the source captureStream
   * and stores the most recent one in `latestInputFrame`. Output writes are
   * driven separately by captureFrame() at the target FPS.
   *
   * Decoupling input from output guarantees constant output FPS regardless of
   * any source-side variance — if the browser briefly produces fewer than
   * target frames, the output side reuses the latest input frame; if it
   * produces more, the extras are simply replaced (the latest wins).
   */
  private async runReaderLoop(): Promise<void> {
    const reader = this.sourceReader;
    if (!reader) return;
    while (this.pipelineActive) {
      let frame: VideoFrame | null = null;
      try {
        const res = await reader.read();
        if (res.done) break;
        frame = res.value as VideoFrame;
      } catch { break; }
      if (!frame) continue;
      // Replace the cached latest input with the freshest frame.
      if (this.latestInputFrame) {
        try { this.latestInputFrame.close(); } catch { /* ignore */ }
      }
      this.latestInputFrame = frame;
    }
    if (this.latestInputFrame) {
      try { this.latestInputFrame.close(); } catch { /* ignore */ }
      this.latestInputFrame = null;
    }
  }

  /** Stops the reader loop and waits for it to unwind before teardown. */
  private async shutdownSource(): Promise<void> {
    this.pipelineActive = false;
    // Cancel the reader to unblock any pending await reader.read().
    if (this.sourceReader) {
      try { this.sourceReader.cancel(); } catch { /* ignore */ }
    }
    if (this.pipelineTask) {
      try { await this.pipelineTask; } catch { /* ignore */ }
      this.pipelineTask = null;
    }
    this.teardownSource();
  }

  private teardownSource(): void {
    this.pipelineActive = false;
    if (this.sourceReader) {
      try { this.sourceReader.releaseLock(); } catch { /* ignore */ }
      this.sourceReader = null;
    }
    if (this.sourceCaptureStream) {
      this.sourceCaptureStream.getTracks().forEach((t) => t.stop());
      this.sourceCaptureStream = null;
    }
    if (this.latestInputFrame) {
      try { this.latestInputFrame.close(); } catch { /* ignore */ }
      this.latestInputFrame = null;
    }
    this.sourceTrack = null;
    this.useProcessorInput = false;
  }
}

export { OUTPUT_SIZES };
export type { RecordingFormat };
